import { useEffect, useLayoutEffect, useState } from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import HandleChange from '../../../../../Helpers/HandleChange';
import {
    CreateIngredient,
    CreateIngredientImage,
    GetIngredientById,
    GetMeasurementUnits,
    UpdateIngredient, UpdateIngredientImage,
} from '../../../../../reduxToolkit/FoodieIngredient/IngredientApi';
import { toast } from 'react-toastify';
import routes from '../../../../../Routes/routes';
import {permissionObj} from '../../../../../Helpers/permission';
import {PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import defaultImage from '../../../../../assets/ImagesDefaults/ic-ingredient-default.svg';
import {base64ToBlob} from '../../../../../utils/Base64ToBlob';
import {DeleteItemsApi} from '../../../../../reduxToolkit/Commons/CommonsApi';
import {TABLES} from '../../../../../Apis/Tables';
import { INGREDIENT_MODEL } from './../Model/Models';
import {errorMsg} from "../../../../../Apis/functions";

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
const AddEditIngredientApp = () => {
    const { id } = useParams();

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    let viewState = location?.state?.view;

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { measurementUnitsAll } = useSelector((state) => state.IngredientSlice);

    const [disabled, setDisabled] = useState(DEV || ( permission?.includes(permissionObj?.WEB_INGREDIENT_READ) && !permission?.includes(permissionObj?.WEB_INGREDIENT_UPDATE) ));
    const [emptyFields, setEmptyFields] = useState(false);
    // image-file
    const [photoPickerFile, setPhotoPickerFile] = useState({...PHOTO_PICKER_MODEL, preview: defaultImage})
    const [imageId, setImageId] = useState('')

    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);

    const [item, setItem] = useState({ ...INGREDIENT_MODEL });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    const [errorCode, setErrorCode] = useState(true);
    const [errorName, setErrorName] = useState(true);

    const [showBtnDelete, setShowBtnDelete] = useState(true);

    /**
     * tep name assignment.
     */
    useLayoutEffect(() => {
        setSteps([t("set_up_ingredient"), t("upload_photo"), t("summary")]);
    }, [permission, t]);

    /**
     * Retrieves the list of measurement units for ingredients.
     */
    useEffect(() => {
        dispatch(GetMeasurementUnits());
    }, []);

    /**
     * Retrieves the data of an ingredient by its ID and sets the data.
     */
    useEffect(() => {
        if (id) {
            getIngredientInfo();
        }
    }, [id]);

    const getIngredientInfo = () => {
        dispatch(GetIngredientById(id)).then(({ payload: { data: { data } } }) => {
            if (data.id){
                setItem({ ...INGREDIENT_MODEL, ...data })
                if(data?.imageId){
                    setImageId(data.imageId);
                    setPhotoPickerFile(prev => ({...prev, id: data.imageId, previewImage: `data:image/png;base64,${data.image}`}));
                }else{
                    setImageId('');
                    setPhotoPickerFile({...PHOTO_PICKER_MODEL, preview: defaultImage});
                }
            }
        });
    }
    /**
     * Validates the view based on permissions; if not authorized, returns a forbidden message.
     */
    useEffect(() => {
        if (DEV) return;

        if (
            (!id && !permission?.includes(permissionObj?.WEB_INGREDIENT_CREATE)) ||
            (id && (!permission?.includes(permissionObj?.WEB_INGREDIENT_UPDATE) && !permission?.includes(permissionObj?.WEB_INGREDIENT_READ) ))
        ) {
            navigate(routes.forbidden);
        }
    }, [DEV, permission, id]);

    /**
     * Function to change the current step.
     * @param step
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * Function to validate the first step: preparing ingredients.
     * @returns {boolean}
     */
    const handleStepSetUp = () => {
        if (!item.isValid()) {
            setEmptyFields(true);
            return false;
        }
        return true;
    };

    /**
     * Function to update and/or create the ingredient. It validates the required fields and handles error codes.
     */
    const handleAddEdit = () => {
        if (!item.isValid()) {
            setEmptyFields(true);
            return;
        }
        let body = item.toAddEdit();
        const action = id ? UpdateIngredient(body) : CreateIngredient(body);
        dispatch(action).then(({ payload: { data: { data, success, code, message } } }) => {
            if (data?.id) {
                const newImageToUpload = photoPickerFile.fileSelected !== null ? photoPickerFile.fileSelected : photoPickerFile?.selfieSrc != null  ? base64ToBlob(photoPickerFile.selfieSrc) : null;

                if (!newImageToUpload) {
                    wasCreateOrUpdate(success, code);
                    return;
                }

                const actionImage = imageId !== '' ? UpdateIngredientImage({imageId: imageId, image: newImageToUpload}) : CreateIngredientImage({ imgData: { ingredientId: data.id }, image: newImageToUpload });
                dispatch(actionImage).then(({ payload: { data: { success, code } } }) => {
                    wasCreateOrUpdate(success, code);
                });
            } else {
                errorMsg(code);
                setErrorCode(!message.toLowerCase().includes("code"));
                setErrorName(!message.toLowerCase().includes("name"));
            }
        });
    };

    const wasCreateOrUpdate = (success, code) => {
        if(success) {
            if (id) {
                toast.success(t('has_been_updated_successfully'));
                getIngredientInfo();
                return;
            }

            toast.success(t('has_been_created_successfully'));
            navigate(routes.foodie.list);
        }else{
            errorMsg(code);
        }
    }

    /**
     * Function that calls the global API to delete an ingredient's image
     */
    const handleDeleteImage = () => {
        dispatch(DeleteItemsApi({ tableName: TABLES.INGREDIENT_IMAGE , body: [item.id] })).then(({payload: {data: { success }}}) => {
            if (success === true) {
                setImageId('');
                setPhotoPickerFile({...PHOTO_PICKER_MODEL, preview: defaultImage})
                setShowBtnDelete(false);
            }
        });
    }


    /**
     * Function to clear the error message from the "name" and "code" fields of the ingredient when values are present.
     * @param evt
     * @constructor
     */
    const HandleChangeControl = (evt) => {
        let aux = evt.target.name;
        if (aux.toLowerCase() == 'code') {
            setErrorCode(true);
        }
        if (aux.toLowerCase() == 'name') {
            setErrorName(true);
        }

        handleChange(evt);
    };

    /**
     * Function to navigate to the list view.
     */
    const handleOnBack = () => {
        navigate( routes.foodie.list, { state: { view: viewState }} );
    }

    /**
     * Function to get the section title.
     * @returns {string|string}
     */
    const getTitle = () => {
        if(!id)
            return "create"
        else{
            return ( DEV || ( permission?.includes(permissionObj?.WEB_INGREDIENT_UPDATE) && permission?.includes(permissionObj?.WEB_INGREDIENT_READ) ) ) ?  "update" : "details"
        }
    }

    return {
        t,
        item,
        measurementUnitsAll,
        handleChange: HandleChangeControl,
        disabled,
        emptyFields,
        activeStep, setActiveStep,
        steps, setSteps,
        photoPickerFile, setPhotoPickerFile,
        id,
        handleStepChange,
        handleStepSetUp,
        handleAddEdit,
        handleDeleteImage,
        DEV,
        permission,
        navigate,
        errorCode,
        errorName,
        showBtnDelete,
        handleOnBack,
        getTitle
    };
};

export default AddEditIngredientApp;
